/** Imports */
import {QueryClient} from '@tanstack/react-query';


/**
 * Browser query client instance.
 *
 * We want to reuse the same query client instance across the app in the browser, but create a new one for each request on the server.
 * This is because the query client holds state, and we don't want to share state between requests on the server.
 * On the browser, we can reuse the same instance because it's a single user interacting with the app.
 * On the server, we need to create a new instance for each request to avoid sharing state between users.
 *
 * @see https://tanstack.com/query/v4/docs/react/QueryClient#instantiating-queryclient
 */
let browserQueryClient: QueryClient | undefined;


/**
 * Make query client options.
 *
 * @see https://tanstack.com/query/v4/docs/react/QueryClient#defaultoptions
 * @returns Query client options
 */
function makeQueryClientOptions() {
    return {
        defaultOptions: {
            queries: {
                staleTime: 30_000,
                refetchOnWindowFocus: false,
            },
            mutations: {
                retry: false,
            },
        },
    };
}

/**
 * Make query client.
 *
 * @returns Query client
 */
export function makeQueryClient() {
    return new QueryClient(makeQueryClientOptions());
}

/**
 * Get query client.
 *
 * On the server, this will create a new query client instance for each request.
 * On the browser, this will reuse the same query client instance across the app.
 *
 * @returns Query client
 */
export function getQueryClient() {
    if (typeof window === 'undefined') return makeQueryClient();
    browserQueryClient ??= makeQueryClient();
    return browserQueryClient;
}
